/* WYE: refresh only recommendations when the main product colour changes.
   Same-origin GETs only. Existing cards retain their explicit quick-add behaviour. */
(() => {
  'use strict';
  if (customElements.get('wye-hybrid-recommendations')) return;
  class WyeHybridRecommendations extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this._revision = 0;
      this._grid = this.querySelector('[data-wye-rec-grid]');
      this._section = this.closest('[data-wye-hybrid-section]');
      this._setVisibility();
      if (!this._grid || Number(this.dataset.lookCount) < 1) return;
      try {
        const values = JSON.parse(this.querySelector('[data-wye-rec-colors]').textContent);
        this._colors = new Map(Object.entries(values).filter(([id, color]) => /^\d+$/.test(id) && typeof color === 'string'));
      } catch { return; }
      const productId = this.dataset.productId || '';
      if (!/^\d+$/.test(productId)) return;
      this._product = document.querySelector(`[data-abl-product-root][data-product-id="${productId}"]`);
      if (!this._product) return;
      this._wantedColor = this._colors.get(this.dataset.currentVariant) || '';
      this._observer = new MutationObserver(() => this._sync());
      this._observer.observe(this._product, {attributes: true, attributeFilter: ['data-current-variant-id']});
      this._sync();
    }
    disconnectedCallback() {
      this._ready = false;
      this._revision++;
      clearTimeout(this._timer);
      this._abort?.abort();
      this._observer?.disconnect();
    }
    _setVisibility() {
      if (this._section && this._grid) this._section.hidden = !this._grid.querySelector('[data-wl-card]');
    }
    _initialiseCards() {
      this._setVisibility();
      this.dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles: true, detail: {sectionId: this.dataset.sectionId}
      }));
    }
    _fallback() {
      const template = this.querySelector('template[data-wye-rec-fallback]');
      if (!template || !this._grid.querySelector('[data-wye-rec-source="look"]')) return;
      this._grid.replaceChildren(template.content.cloneNode(true));
      this._initialiseCards();
    }
    _sync() {
      const id = this._product?.dataset.currentVariantId || '';
      if (!this._colors.has(id)) return;
      const color = this._colors.get(id);
      // Size-only changes never request a new recommendation set.
      if (color === this._wantedColor) return;
      this._wantedColor = color;
      const revision = ++this._revision;
      clearTimeout(this._timer);
      this._abort?.abort();
      // Do not leave a different colour's outfit visible during a failed/slow request.
      this._fallback();
      this._timer = setTimeout(() => this._refresh(id, revision), 120);
    }
    async _refresh(id, revision) {
      if (!this.isConnected || revision !== this._revision) return;
      const controller = new AbortController();
      this._abort = controller;
      const timeout = setTimeout(() => controller.abort(), 8000);
      this.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(window.location.href);
        if (!url.pathname.includes('/products/')) return;
        url.hash = '';
        url.searchParams.delete('option_values');
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('variant', id);
        // The current URL retains the preview theme, locale, and market context.
        const response = await fetch(url.href, {
          method: 'GET', credentials: 'same-origin', signal: controller.signal,
          headers: {Accept: 'text/html'}, cache: 'no-store'
        });
        if (!response.ok) throw new Error('Recommendation response unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = [...html.querySelectorAll('wye-hybrid-recommendations')].find(element =>
          element.dataset.sectionId === this.dataset.sectionId && element.dataset.productId === this.dataset.productId
        );
        const grid = next?.querySelector('[data-wye-rec-grid]');
        if (!grid) throw new Error('Recommendation section missing');
        if (!this.isConnected || revision !== this._revision) return;
        this._grid.replaceChildren(...[...grid.childNodes].map(node => document.importNode(node, true)));
        const fallback = next.querySelector('template[data-wye-rec-fallback]');
        const currentFallback = this.querySelector('template[data-wye-rec-fallback]');
        if (fallback && currentFallback) currentFallback.content.replaceChildren(document.importNode(fallback.content, true));
        this.dataset.currentVariant = id;
        delete this.dataset.refreshError;
        this._initialiseCards();
      } catch (error) {
        if (revision === this._revision && this.isConnected) {
          this.dataset.refreshError = error.name === 'AbortError' ? 'timeout' : 'unavailable';
          this._fallback();
        }
      } finally {
        clearTimeout(timeout);
        if (revision === this._revision && this.isConnected) this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('wye-hybrid-recommendations', WyeHybridRecommendations);
})();
